import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient from '../../api/apiClient';
import EmployeeHeader from '../../components/hris/employee/EmployeeHeader';
import EmployeeFilters from '../../components/hris/employee/EmployeeFilters';
import EmployeeTable from '../../components/hris/employee/EmployeeTable';
import DeleteDialog from '../../components/hris/employee/DeleteDialog';
import { containerVariants, itemVariants } from '../../utils/animations';

export default function EmployeeList() {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDept, setFilterDept] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const data = await apiClient.get<any>('/hris/employees');
      setEmployees(data.employees || []);
      setDepartments(data.departments || []);
      setPositions(data.positions || []);
    } catch (error) {
      // Error loading data
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await apiClient.delete(`/hris/employees/${deleteId}`);
      setEmployees(employees.filter(e => e.id !== deleteId));
      setDeleteId(null);
    } catch (error) {
      // Error deleting employee
    }
  };

  const getDepartmentName = deptId => {
    return departments.find(d => d.id === deptId)?.name || '-';
  };

  const getPositionName = posId => {
    return positions.find(p => p.id === posId)?.name || '-';
  };

  const filteredEmployees = employees.filter(emp => {
    const matchSearch =
      emp.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.employee_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchDept = filterDept === 'all' || emp.department_id === filterDept;
    const matchStatus = filterStatus === 'all' || emp.status === filterStatus;
    return matchSearch && matchDept && matchStatus;
  });

  return (
    <motion.div
      className="space-y-4 md:space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div variants={itemVariants}>
        <EmployeeHeader />
      </motion.div>
      <motion.div variants={itemVariants}>
        <EmployeeFilters
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filterDept={filterDept}
          setFilterDept={setFilterDept}
          filterStatus={filterStatus}
          setFilterStatus={setFilterStatus}
          departments={departments}
        />
      </motion.div>
      <motion.div variants={itemVariants}>
        <EmployeeTable
          employees={filteredEmployees}
          loading={loading}
          getDepartmentName={getDepartmentName}
          getPositionName={getPositionName}
          onDelete={setDeleteId}
        />
      </motion.div>
      <DeleteDialog show={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} />
    </motion.div>
  );
}
